import type { TokenRow } from "@kinetiq/wire";
import { useEffect, useId, useRef, useState } from "react";
import { isApiError } from "../../api/errors.ts";
import { fetchSearch } from "../../api/market.ts";
import { navigate } from "../../app/router.ts";
import { shortAddr, usd } from "../../lib/format.ts";
import { Marks } from "../../ui/Marks.tsx";
import { Num } from "../../ui/Num.tsx";
import { Spinner } from "../../ui/Spinner.tsx";
import { TokenIdentity } from "../token/TokenIdentity.tsx";
import s from "./search.module.css";
import {
  SEARCH_DEBOUNCE_MS,
  SEARCH_PAGE,
  type SearchOption,
  type SearchResult,
  coverageNote,
  hrefForOption,
  isRunnable,
  isUnknownAddress,
  nextActive,
  normaliseQuery,
  phaseOf,
} from "./searchModel.ts";

/**
 * THE SEARCH BOX — the app's ONE navigation control, in the top bar.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 * IT REPLACES THE ADDRESS BOX RATHER THAN SITTING BESIDE IT
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 * The bar used to carry a paste-an-address box whose docstring said, correctly for the day it was written,
 * that a box accepting a NAME and returning nothing would report a missing SUBSYSTEM as an empty result.
 * `GET /v1/search` exists now, so that reasoning has expired — and adding a second input beside the first
 * would leave the bar with two boxes that both look like search and answer different questions. One box:
 * type a name or a symbol and get a ranked list; paste an address and get that mint, plus the wallet
 * destination the route cannot decide for you (a mint and an owner are both 32-byte base58, and guessing
 * sends half of all pastes to a token page for a wallet — a real 200 with nothing in it).
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 * "STILL TYPING" AND "NOTHING MATCHES" ARE DIFFERENT STATES AND ARE DRAWN DIFFERENTLY
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 * Every phase comes from `phaseOf`, which decides on the query the held answer WAS FOR versus the query in
 * the box. An empty panel while a request is in flight is the single most common way a working search reads
 * as a broken one, and a loading flag alone cannot separate them: it goes false the moment a stale answer
 * lands. The states are `idle · tooShort · pending · results · empty · error`, and `empty` is the only one
 * that claims nothing matched.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 * THE RANKING IS A SAFETY PROPERTY, SO THE ROWS CARRY THE THING IT RANKS ON
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 * Minting a token whose symbol is exactly `USDC` costs a few cents, so the server ranks by liquidity and
 * volume with match quality as a bounded tilt — never by string similarity, which would put impersonators
 * above the tokens they impersonate. Every row therefore shows LIQUIDITY beside the identity: the number the
 * order is built on is the number the user can check it against, and a dust clone with a perfect ticker is
 * visibly a dust clone. `Num` renders an absent one as "—" with a reason, never as `0`.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 * ONE REQUEST IN FLIGHT, AND THE NEWEST QUERY ALWAYS WINS
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 * The effect is keyed on the normalised query: its cleanup clears the pending debounce AND aborts the
 * in-flight request, so a superseded answer can never land on top of a newer one. An `aborted` failure is
 * therefore not an error and is never shown — the caller moved on, which is not a thing to tell them about.
 */
export function SearchBox() {
  const [raw, setRaw] = useState("");
  const [result, setResult] = useState<SearchResult | null>(null);
  const [error, setError] = useState<{ query: string; message: string } | null>(null);
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const query = normaliseQuery(raw);

  useEffect(() => {
    if (!isRunnable(query)) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      fetchSearch(query, SEARCH_PAGE, controller.signal)
        .then((r) => {
          setResult({
            query: r.query,
            count: r.count,
            matched: r.matched,
            scored: r.scored,
            identityPending: r.identityPending,
            tokens: r.tokens,
          });
          setError(null);
          setActive(0);
        })
        .catch((err: unknown) => {
          // A cancelled request is the newer keystroke doing its job, not a failure to report.
          if (isApiError(err) && err.code === "aborted") return;
          const message = isApiError(err) ? err.message : "the search failed";
          setError({ query, message });
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  const phase = phaseOf({ query, result, error });
  const options = phase.kind === "results" || phase.kind === "empty" ? phase.options : [];
  const activeIndex = options.length === 0 ? 0 : Math.min(active, options.length - 1);
  const expanded = open && phase.kind !== "idle";

  const go = (option: SearchOption | undefined) => {
    if (option === undefined) return;
    navigate(hrefForOption(option));
    setRaw("");
    setResult(null);
    setError(null);
    setOpen(false);
    inputRef.current?.blur();
  };

  return (
    <div className={s.box}>
      <div className={s.field}>
        <input
          ref={inputRef}
          className={s.input}
          value={raw}
          // ⚠️ `text`, NOT `search`, AND THAT IS A BEHAVIOUR DECISION. On `type="search"` the browser handles
          // Escape itself and CLEARS the box — measured in Chromium — which silently overrides the two-step
          // below and throws the query away on the keypress that was meant to only dismiss the panel. The one
          // thing it buys — the native clear cross — is drawn in the platform's colours rather than the
          // app's, so `search` was costing a keystroke's behaviour for an affordance we would have had to
          // suppress anyway.
          type="text"
          role="combobox"
          aria-expanded={expanded}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={
            expanded && options.length > 0 ? `${listId}-${activeIndex}` : undefined
          }
          aria-label="search tokens by symbol, name or address"
          placeholder="search a symbol, name or address"
          spellCheck={false}
          autoComplete="off"
          onChange={(e) => {
            setRaw(e.currentTarget.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          // NOT a blur timer. The options call `preventDefault` on mousedown so focus never leaves during a
          // click — a `setTimeout` here would be a race between the blur and the click, and it is the click
          // that loses on a slow machine.
          onBlur={() => setOpen(false)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault();
              setOpen(true);
              setActive(
                nextActive(activeIndex, options.length, e.key === "ArrowUp" ? "up" : "down"),
              );
              return;
            }
            if (e.key === "Enter") {
              // Only ever navigates to something the panel is SHOWING. With no options there is nothing to
              // open, and inventing a destination — the raw string as a mint, say — is how a typo becomes a
              // token page for an address that does not exist.
              e.preventDefault();
              go(options[activeIndex]);
              return;
            }
            if (e.key === "Escape") {
              // First Escape closes the panel; a second clears the box. Clearing on the first press throws
              // away a query the user may have only wanted to look away from.
              if (expanded) setOpen(false);
              else setRaw("");
            }
          }}
        />
        {phase.kind === "pending" ? <Spinner className={s.spinner} /> : null}
      </div>

      {expanded ? (
        <div className={s.panel}>
          {/* biome-ignore lint/a11y/useFocusableInteractive: the listbox is NOT a tab stop in this pattern —
              see the note on `role` below. */}
          <ul
            className={s.list}
            id={listId}
            // THE POPUP THE INPUT OWNS. A `<ul>` is the right element and `listbox` is the role that tells a
            // screen reader this list is the combobox's popup rather than page content; no HTML element means
            // that on its own, and in this pattern the list is NOT a tab stop — focus stays in the input and
            // `aria-activedescendant` names the active option, which is what lets the arrow keys drive it
            // without fighting the browser's own focus order.
            // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: the pattern above.
            // biome-ignore lint/a11y/useSemanticElements: the pattern above.
            role="listbox"
            aria-label="search results"
          >
            {options.map((option, i) => (
              <li key={option.kind === "wallet" ? `wallet:${option.owner}` : option.mint}>
                <a
                  className={i === activeIndex ? `${s.option} ${s.optionActive}` : s.option}
                  href={hrefForOption(option)}
                  id={`${listId}-${i}`}
                  // AN ANCHOR CARRYING `role="option"`, and each half is deliberate. The anchor is what makes
                  // a result middle-clickable and copy-link-able — the same reason every navigation on this
                  // surface is a link rather than a button. The role is what puts it in the listbox above.
                  // biome-ignore lint/a11y/useSemanticElements: there is no element that is both.
                  role="option"
                  aria-selected={i === activeIndex}
                  // Keeps focus in the input, so the click lands before any blur can close the panel. The
                  // anchor is still an anchor: middle-click and copy-link both work.
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setActive(i)}
                  onClick={(e) => {
                    e.preventDefault();
                    go(option);
                  }}
                >
                  {option.kind === "wallet" ? (
                    <WalletOption owner={option.owner} />
                  ) : (
                    <TokenOption row={option.row} mint={option.mint} />
                  )}
                </a>
              </li>
            ))}
          </ul>
          <PanelNote phase={phase} query={query} />
        </div>
      ) : null}
    </div>
  );
}

/** One token. The identity is `TokenIdentity` — the same component the board and the wallet render — so a
 *  mint cannot look like one thing here and another thing one click later. `link={false}` because the whole
 *  row is already the link; a nested anchor is invalid markup and swallows the row's own click. */
function TokenOption({ row, mint }: { row: TokenRow | null; mint: string }) {
  if (row === null) {
    return (
      <>
        <TokenIdentity mint={mint} symbol={null} name={null} image={null} link={false} />
        <span className={s.meta}>no identity yet</span>
      </>
    );
  }
  return (
    <>
      <TokenIdentity
        mint={row.mint}
        symbol={row.symbol}
        name={row.name}
        image={row.image}
        link={false}
      />
      {/* THE REGISTRY'S TICK, HERE AS WELL AS ON THE BOARD — and search is where it matters MOST. Minting a
          token whose symbol is exactly `USDC` costs a few cents, so a search for a symbol is the one place a
          user is handed several mints wearing one name and asked to pick. Liquidity (below) is the ranking
          and it is BUYABLE; the registry's claim is not. Rendered as the tick alone, with the same title, so
          the two surfaces cannot say different things about one mint. */}
      <Marks registry={row.registry} clones={null} trust={row.trust} symbol={row.symbol} />
      {/* THE NUMBER THE ORDER IS BUILT ON. It is here so a perfect symbol match with no depth is visibly
          that, rather than just the first row. */}
      <Num
        className={s.liquidity}
        value={row.liquidityUsd}
        format={usd}
        unknown="no pool reserves for this mint yet"
      />
    </>
  );
}

/** The destination the route cannot decide. It is spelled as an action rather than as a result, because
 *  nothing was looked up: this is an address the user pasted, offered as a wallet. */
function WalletOption({ owner }: { owner: string }) {
  return (
    <>
      <span className={s.walletIcon} aria-hidden="true">
        ◎
      </span>
      <span className={s.walletText}>
        open <code className={s.mono}>{shortAddr(owner, 4)}</code> as a wallet
      </span>
    </>
  );
}

/** The line under the rows. It is where every honest statement about the answer goes, and each phase gets its
 *  own words — an empty panel with no explanation is indistinguishable from a panel that failed to render. */
function PanelNote({
  phase,
  query,
}: {
  phase: ReturnType<typeof phaseOf>;
  query: string;
}) {
  if (phase.kind === "tooShort") {
    return (
      <p className={s.note}>
        Keep typing — {phase.need} more character{phase.need === 1 ? "" : "s"}.
      </p>
    );
  }
  if (phase.kind === "pending") {
    return <p className={s.note}>Searching “{query}”…</p>;
  }
  if (phase.kind === "error") {
    return <p className={s.noteError}>{phase.message}</p>;
  }
  if (phase.kind === "empty") {
    return (
      <p className={s.note}>
        Nothing matches “{query}”. Tokens are searchable by symbol or name once they have traded in
        the window we hold; anything older is still reachable by pasting its mint address.
      </p>
    );
  }
  if (phase.kind === "results") {
    // A PASTED ADDRESS WE HOLD NO IDENTITY FOR IS NOT AN EMPTY RESULT — and it is TWO different facts a
    // keystroke apart, so the panel says which one it has.
    //
    // ⚠️ ON THE FIRST LOOK, `identityPending` DOES NOT SEPARATE "a mint we have not enriched yet" from "an
    // address that is not a token at all": the field is "we hold no identity ROW", and the row is absent in
    // both cases. MEASURED on the live serve — a pasted WALLET address came back exactly like an unenriched
    // mint. So this note states both possibilities rather than picking one, and points at the destination
    // that settles it.
    if (phase.result.identityPending) {
      return (
        <p className={s.note}>
          We hold no identity for this address yet. If it is a mint, the name and logo fill in once
          the enrichment this search queued lands; if it is a wallet or a program, they never will —
          open it as a wallet instead.
        </p>
      );
    }
    // AND HERE IS THE ANSWER, once that enrichment has run: an identity row that EXISTS and is empty.
    // Enrichment looked and found no token, which is a different statement from "not yet" and is the only
    // case in which the panel can say so.
    if (isUnknownAddress(phase.result)) {
      return (
        <p className={s.note}>
          Enrichment found no token at this address — it is a wallet or a program. Open it as a
          wallet.
        </p>
      );
    }
    const note = coverageNote(phase.result);
    return note === null ? null : <p className={s.note}>{note}</p>;
  }
  return null;
}
